import { useT, type I18nKey } from "@/lib/i18n";
import type { WordDetail } from "@/lib/words";

/**
 * POS-dispatched inflection display. One card type per gramatical class,
 * sharing a common wrapper + cell style. SCHEMA.md §6 + §8 is the spec.
 *
 * Shapes handled:
 *   - NOUN: the existing 2×2 (indefinite/articulated × singular/plural)
 *   - VERB regular:  4 slots: prezent_1sg, prezent_3sg, participiu, imperativ_2sg
 *   - VERB impersonal: prezent_3sg, participiu, imperfect_3sg, perfect_3sg
 *   - ADJ / PRON-possessive / NUMERAL-ordinal: 2×2 gender×number (m_sg, f_sg, m_pl, f_pl)
 *   - PRON-personal: vertical 5-case list (nominativ + acuzativ ×2 + dativ ×2)
 *   - NUMERAL-cardinal: {m, f} side-by-side
 *
 * Returns `null` when the word has nothing to display (POS without inflection
 * or missing data) so callers don't need to guard.
 */
export function InflectionCard({ word }: { word: WordDetail }) {
  // Dispatch by POS. The forms object + scalar fields together determine
  // which variant we render.
  if (word.pos === "NOUN") return <NounInflection word={word} />;
  if (word.pos === "VERB") return <VerbInflection word={word} />;
  if (word.pos === "ADJ") return <GenderNumberInflection word={word} />;
  if (word.pos === "PRON") {
    // Two sub-variants of PRON: personal (has `nominativ` key) vs. possessive
    // (has `m_sg` key). Detect via the forms-object shape.
    const forms = word.forms;
    if (forms && "nominativ" in forms) return <PersonalPronounInflection word={word} />;
    if (forms && "m_sg" in forms) return <GenderNumberInflection word={word} />;
    return null;
  }
  if (word.pos === "NUMERAL") {
    const forms = word.forms;
    if (forms && "m_sg" in forms) return <GenderNumberInflection word={word} />;
    if (forms && ("m" in forms || "f" in forms)) return <CardinalNumeralInflection word={word} />;
    return null;
  }
  // PHRASE, PREP, CONJ, ADV, ARTICLE, INTERJ — no inflection display.
  return null;
}

// ─── Shared wrappers ─────────────────────────────────

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="mb-2 pl-0.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-text-3">{children}</div>
  );
}

function CardFrame({ children }: { children: React.ReactNode }) {
  return <section className="rounded-[var(--r-lg)] border border-border bg-surface p-4">{children}</section>;
}

export function Chips({ children }: { children: React.ReactNode }) {
  return <div className="mb-3 flex flex-wrap gap-1.5">{children}</div>;
}

export function Chip({ label, accent = false }: { label: string; accent?: boolean }) {
  return (
    <span
      className="inline-flex items-center rounded-full border border-border px-2.5 py-0.5 text-[11px] font-medium"
      style={
        accent
          ? { background: "var(--accent-soft)", color: "var(--accent)", borderColor: "transparent" }
          : { color: "var(--text-2)" }
      }
    >
      {label}
    </span>
  );
}

// ─── NOUN (existing 2×2, relocated here) ──────────────

function NounInflection({ word }: { word: WordDetail }) {
  const t = useT();
  const sing = word.lemma;
  const plur = word.plural;
  const singArt = word.articulated;
  const plurArt = word.articulatedPlural;

  // Nothing to show if we have zero inflectional data — collapse rather
  // than render a table of dashes.
  if (!plur && !singArt && !plurArt) return null;

  return (
    <CardFrame>
      <SectionLabel>{t("word_detail.forms")}</SectionLabel>
      {word.gender && (
        <Chips>
          <Chip
            accent
            label={
              word.gender === "F"
                ? t("inflection.gender_f")
                : word.gender === "M"
                  ? t("inflection.gender_m")
                  : t("inflection.gender_n")
            }
          />
        </Chips>
      )}
      <div className="overflow-hidden rounded-[var(--r-sm)] border border-border">
        <table className="w-full border-collapse text-[14px]">
          <thead>
            <tr className="bg-[color:var(--surface-2)] text-[11px] font-semibold uppercase tracking-[0.05em] text-text-2">
              <th className="w-[30%] px-3 py-2 text-left font-semibold">&nbsp;</th>
              <th className="px-3 py-2 text-left font-semibold">{t("inflection.indefinite")}</th>
              <th className="px-3 py-2 text-left font-semibold">{t("inflection.articulated")}</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-t border-border">
              <th className="px-3 py-2 text-left text-[12px] font-medium text-text-3">{t("inflection.singular")}</th>
              <td className="px-3 py-2">{sing}</td>
              <td className="px-3 py-2 text-[color:var(--accent)]">{singArt ?? "—"}</td>
            </tr>
            {(plur || plurArt) && (
              <tr className="border-t border-border">
                <th className="px-3 py-2 text-left text-[12px] font-medium text-text-3">{t("inflection.plural")}</th>
                <td className="px-3 py-2">{plur ?? "—"}</td>
                <td className="px-3 py-2 text-[color:var(--accent)]">{plurArt ?? "—"}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </CardFrame>
  );
}

// ─── VERB ─────────────────────────────────────────────

function VerbInflection({ word }: { word: WordDetail }) {
  const t = useT();
  const forms = word.forms;

  // Slot list differs for impersonal verbs (weather verbs have no 1sg form).
  const slots: Array<{ key: string; labelKey: I18nKey }> = word.impersonal
    ? [
        { key: "prezent_3sg", labelKey: "inflection.verb_prezent_3sg" },
        { key: "participiu", labelKey: "inflection.verb_participiu" },
        { key: "imperfect_3sg", labelKey: "inflection.verb_imperfect_3sg" },
        { key: "perfect_3sg", labelKey: "inflection.verb_perfect_3sg" },
      ]
    : [
        { key: "prezent_1sg", labelKey: "inflection.verb_prezent_1sg" },
        { key: "prezent_3sg", labelKey: "inflection.verb_prezent_3sg" },
        { key: "participiu", labelKey: "inflection.verb_participiu" },
        { key: "imperativ_2sg", labelKey: "inflection.verb_imperativ_2sg" },
      ];

  const hasAny = forms && slots.some((s) => forms[s.key]);
  if (!hasAny && !word.conjugation && !word.reflexive && !word.impersonal) return null;

  return (
    <CardFrame>
      <SectionLabel>{t("word_detail.forms")}</SectionLabel>
      <Chips>
        {word.conjugation != null && (
          <Chip accent label={t("inflection.conjugation_value", { n: toRoman(word.conjugation) })} />
        )}
        {word.reflexive && <Chip label={t("inflection.reflexive_badge")} />}
        {word.impersonal && <Chip label={t("inflection.impersonal_badge")} />}
      </Chips>
      {hasAny && (
        <div className="overflow-hidden rounded-[var(--r-sm)] border border-border">
          <table className="w-full border-collapse text-[14px]">
            <tbody>
              {slots.map((s, i) => {
                const v = forms?.[s.key];
                if (!v) return null;
                return (
                  <tr key={s.key} className={i > 0 ? "border-t border-border" : ""}>
                    <th className="w-[45%] px-3 py-2 text-left text-[12px] font-medium text-text-3">{t(s.labelKey)}</th>
                    <td className="px-3 py-2 font-mono">{v}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </CardFrame>
  );
}

// 1-4 → I/II/III/IV. Colocated here since this is the only callsite.
function toRoman(n: number): string {
  return (["I", "II", "III", "IV"] as const)[n - 1] ?? String(n);
}

// ─── ADJ / PRON-possessive / NUMERAL-ordinal (2×2) ───

function GenderNumberInflection({ word }: { word: WordDetail }) {
  const t = useT();
  const forms = word.forms ?? {};
  const mSg = forms["m_sg"];
  const fSg = forms["f_sg"];
  const mPl = forms["m_pl"];
  const fPl = forms["f_pl"];
  if (!mSg && !fSg && !mPl && !fPl) return null;

  // Invariable forms print the same value in every slot — collapse to a
  // single-cell row so the reader immediately sees "all forms equal" rather
  // than four identical cells.
  const isInvariable = word.agreementType === "INVARIABLE" || (mSg === fSg && fSg === mPl && mPl === fPl && !!mSg);

  return (
    <CardFrame>
      <SectionLabel>{t("word_detail.forms")}</SectionLabel>
      {word.agreementType && (
        <Chips>
          <Chip accent label={agreementLabel(word.agreementType, t)} />
        </Chips>
      )}
      {isInvariable ? (
        <div className="rounded-[var(--r-sm)] border border-border px-3 py-3 text-center text-[15px] font-medium">
          {mSg ?? fSg ?? mPl ?? fPl}
        </div>
      ) : (
        <div className="overflow-hidden rounded-[var(--r-sm)] border border-border">
          <table className="w-full border-collapse text-[14px]">
            <thead>
              <tr className="bg-[color:var(--surface-2)] text-[11px] font-semibold uppercase tracking-[0.05em] text-text-2">
                <th className="w-[30%] px-3 py-2 text-left font-semibold">&nbsp;</th>
                <th className="px-3 py-2 text-left font-semibold">{t("inflection.singular")}</th>
                <th className="px-3 py-2 text-left font-semibold">{t("inflection.plural")}</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-t border-border">
                <th className="px-3 py-2 text-left text-[12px] font-medium text-text-3">{t("inflection.masculine")}</th>
                <td className="px-3 py-2">{mSg ?? "—"}</td>
                <td className="px-3 py-2">{mPl ?? "—"}</td>
              </tr>
              <tr className="border-t border-border">
                <th className="px-3 py-2 text-left text-[12px] font-medium text-text-3">{t("inflection.feminine")}</th>
                <td className="px-3 py-2">{fSg ?? "—"}</td>
                <td className="px-3 py-2">{fPl ?? "—"}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </CardFrame>
  );
}

function agreementLabel(t: NonNullable<WordDetail["agreementType"]>, tr: ReturnType<typeof useT>): string {
  if (t === "FOUR_FORMS") return tr("inflection.agreement_four_forms");
  if (t === "TWO_FORMS") return tr("inflection.agreement_two_forms");
  return tr("inflection.agreement_invariable");
}

// ─── PRON personal (5 cases) ──────────────────────────

function PersonalPronounInflection({ word }: { word: WordDetail }) {
  const t = useT();
  const forms = word.forms ?? {};
  const rows: Array<{ key: string; labelKey: I18nKey }> = [
    { key: "nominativ", labelKey: "inflection.pron_nominativ" },
    { key: "acuzativ_accentuat", labelKey: "inflection.pron_acc_stressed" },
    { key: "acuzativ_neaccentuat", labelKey: "inflection.pron_acc_unstressed" },
    { key: "dativ_accentuat", labelKey: "inflection.pron_dat_stressed" },
    { key: "dativ_neaccentuat", labelKey: "inflection.pron_dat_unstressed" },
  ];
  if (!rows.some((r) => forms[r.key])) return null;

  return (
    <CardFrame>
      <SectionLabel>{t("word_detail.forms")}</SectionLabel>
      <div className="overflow-hidden rounded-[var(--r-sm)] border border-border">
        <table className="w-full border-collapse text-[14px]">
          <tbody>
            {rows.map((r, i) => {
              const v = forms[r.key];
              if (!v) return null;
              return (
                <tr key={r.key} className={i > 0 ? "border-t border-border" : ""}>
                  <th className="w-[45%] px-3 py-2 text-left text-[12px] font-medium text-text-3">{t(r.labelKey)}</th>
                  <td className="px-3 py-2 font-mono">{v}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </CardFrame>
  );
}

// ─── NUMERAL cardinal (m/f) ──────────────────────────

function CardinalNumeralInflection({ word }: { word: WordDetail }) {
  const t = useT();
  const forms = word.forms ?? {};
  const m = forms["m"];
  const f = forms["f"];
  if (!m && !f) return null;

  return (
    <CardFrame>
      <SectionLabel>{t("word_detail.forms")}</SectionLabel>
      <div className="grid grid-cols-2 gap-2">
        <div className="rounded-[var(--r-sm)] border border-border px-3 py-3 text-center">
          <div className="mb-1 text-[11px] text-text-3">{t("inflection.m_short")}</div>
          <div className="text-[15px] font-medium">{m ?? "—"}</div>
        </div>
        <div className="rounded-[var(--r-sm)] border border-border px-3 py-3 text-center">
          <div className="mb-1 text-[11px] text-text-3">{t("inflection.f_short")}</div>
          <div className="text-[15px] font-medium">{f ?? "—"}</div>
        </div>
      </div>
    </CardFrame>
  );
}
